import React, {
  useEffect,
  useRef,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import composeRefs from '@seznam/compose-react-refs';
import { useTranslation } from '../../i18n';
import { useSubbuzzes } from './useSubbuzzes';
import { useAIGCWrapper } from '../../hooks/useAIGCWrapper';
import { useAmazonSubtagModifier } from '../../hooks/useAmazonSubtagModifier';
import { useBullwhip } from '../../hooks/useBullwhip';
import { useButtonPostTap } from '../../hooks/useButtonPostTap';
import { useContentGating } from '../../hooks/useContentGating';
import { useSkimlinksXcustModifier } from '../../hooks/useSkimlinksXcustModifier';
import { useAffiliateLinkTracking } from '../../hooks/analytics/useAffiliateLinkTracking';
import { useLinkTracking } from '../../hooks/analytics/useLinkClickTracking';
import useNavTracking from '../../hooks/analytics/useNavTracking';
import { useProductClicks } from '../../hooks/useProductClicks';
import { useProductImageHref } from '../../hooks/useProductImageHref';
import { useShoppingStickers } from '../../hooks/useShoppingStickers';
import { SubbuzzInsert } from '../SubbuzzInsert';
import {
  attachClientImpressionHandler,
  attachClientSubbuzzImpressionHandler,
} from '../../hooks/analytics/client-event-tracking';
import withInlineAds from './withInlineAds';
import DestinationContext from '../../contexts/destination';
import { isNews } from '../../utils/isNews';
import { isGeneratorPage } from '../../utils/isGeneratorPage';
import AdsSsrPlacement from '../Ads/AdsSsrPlacement';
import useShoppingDividers from '../ShoppingDivider/useShoppingDividers';
import useShoppingNumberBg from '../ShoppingNumberBg';
import { isExCoEmbed, fixExCoIframeStyle, hideExCoEmbed } from '../../utils/exCoPlayer';
import { isIDNML } from '../../utils/idnml';

export const SubbuzzHTML = ({
  html = '',
  buzz,
  index,
  isContentGate = false,
}) => {
  const ref = useAffiliateLinkTracking({ buzz });
  const clickRef = useAmazonSubtagModifier({ buzz });
  const clickAIGCRef = useAIGCWrapper({ buzz, index });
  const clickBButtonPostTapRef = useButtonPostTap({ buzz });
  const clickSkimlinksRef = useSkimlinksXcustModifier({ buzz });
  const clickLinkRef = useLinkTracking(buzz, index);
  const clickProductLinkRef = useProductClicks({ buzz });
  const clickImageRef = useProductImageHref({ buzz });
  const bullwhipRef = useBullwhip();
  const { contentGateRef, gateObservable } = useContentGating(buzz, isContentGate);
  const showFullWidth = isGeneratorPage(buzz) && buzz?.sub_buzzes[index]?.format_name === 'ai_generator';

  useEffect(() => {
    /*
     * On the client `html` will always be an empty string so
     * this data should be queried using DOM APIs.
     */
    let detachImpressionTracking;

    if (ref && ref.current) {
      // SRA returns markup with an empty div for share bar to be inserted
      // Append the share bar and bind share functionality

      // Add impression tracking to subbuzz unit
      // ID will be present in subbuzz HTML
      const anchor = ref.current.querySelector('div.subbuzz-anchor');
      const isNewsRelatedLinks =
        isNews(buzz) &&
        ref.current.querySelector('.subbuzz-bfp--related_links');
      const subbuzzId = anchor && anchor.id ? anchor.id : null;

      // Fix ex.co player iframe style if the subbuzz is an ex.co embed
      // hide the ex.co embed if the user is not in an IDNML region (UK, IE, GB)
      if (isExCoEmbed(buzz.sub_buzzes?.[index]) && ref.current) {
        if (isIDNML({ isBFN: isNews(buzz) })) {
          fixExCoIframeStyle(ref.current);
        } else {
          hideExCoEmbed(ref.current);
        }
      }

      if (!isNewsRelatedLinks) {
        detachImpressionTracking = attachClientSubbuzzImpressionHandler(
          ref.current,
          buzz,
          {
            item_name: subbuzzId,
            item_type: 'subbuzz',
            position_in_unit: Number(index),
            unit_type: 'buzz_body',
            unit_name: buzz.id,
            subunit_name: '',
            subunit_type: '',
          }
        );
      } else {
        const relatedLinks = ref.current.querySelectorAll(
          '.bfp-related-links__link'
        );
        relatedLinks.forEach((link, i) => {
          const isInternalNewsLink = link.href.match(
            /^(http:\/\/|https:\/\/)?(www|stage)?.buzzfeednews\.com/g
          );
          attachClientImpressionHandler(link, buzz, {
            subunit_name: 'more_on_this',
            subunit_type: 'package',
            unit_type: 'buzz_body',
            unit_name: buzz.id,
            item_name: link.href,
            item_type: 'card',
            position_in_unit: Number(index),
            position_in_subunit: Number(i),
            target_content_type: isInternalNewsLink ? 'buzz' : 'url',
            target_content_id: link.href,
          });
        });
      }
    }

    return () => {
      if (typeof detachImpressionTracking === 'function') {
        detachImpressionTracking();
      }
    };
  }, [buzz, index, ref]);

  return (
    <div className={`js-subbuzz-wrapper ${showFullWidth ? 'subbuzz-full-width' : ''}`}>
      <div
        dangerouslySetInnerHTML={{ __html: html }}
        ref={composeRefs(
          ref,
          clickRef,
          clickAIGCRef,
          clickSkimlinksRef,
          clickLinkRef,
          clickProductLinkRef,
          clickBButtonPostTapRef,
          clickImageRef,
          bullwhipRef,
          contentGateRef,
          gateObservable
        )}
      />
      <SubbuzzInsert
        buzz={buzz}
        subbuzzEl={ref}
        index={index}
      />
    </div>
  );
};

export const Subbuzzes = ({ buzz, subbuzzData = {} }) => {
  const { destination } = useContext(DestinationContext);
  const subbuzzRef = useRef(null);
  const t3App = useSubbuzzes(subbuzzData.assets);

  useNavTracking();
  const { t } = useTranslation('common');
  const displaySubbuzzes = subbuzzData.subbuzzes
    ? [...subbuzzData.subbuzzes]
    : [];

  const classFormats = ['list', 'long'];
  const formatClass = classFormats.reduce((c, format) => {
    if (buzz.format.page_type.includes(format)) {
      c += `buzz--${format}`;
    }
    return c;
  }, '');

  // append updates title HTML to subbuzz data for breaking posts
  if (buzz.format.page_type === 'breaking' && displaySubbuzzes.length) {
    const firstUpdate = displaySubbuzzes.find((s) =>
      s.includes('buzz__update')
    );
    if (firstUpdate) {
      const firstUpdateIndex = displaySubbuzzes.indexOf(firstUpdate);
      displaySubbuzzes.splice(
        firstUpdateIndex,
        0,
        `<h2 class="buzz__update-title">${t('updates')}</h2>`
      );
    }
  }

  // Initialize T3 app
  useEffect(() => {
    if (t3App && t3App.init) {
      t3App.init(buzz);
    }

    return () => {
      if (t3App && t3App.stopAll) {
        t3App.stopAll();
      }
    };
  }, [buzz, t3App]);

  const isUsShopping = !!buzz.isShopping && buzz.country_code === 'en-us';
  // Inject decorative dividers between adjacent subbuzzes on US shopping pages.
  useShoppingDividers(subbuzzRef, isUsShopping);
  // Inject a decorative SVG behind every list number on US shopping pages.
  useShoppingNumberBg(subbuzzRef, isUsShopping);
  useShoppingStickers(subbuzzRef, isUsShopping, buzz);

  return (
    <div
      className={`
        ${formatClass}
        buzz--${buzz.category}
        subbuzzes-wrapper
        subbuzzes--${destination}
      `}
      ref={subbuzzRef}
    >
      {displaySubbuzzes.map((sb, i) => (
        <React.Fragment key={i}>
          <SubbuzzHTML
            html={sb}
            buzz={buzz}
            index={i}
            isContentGate={i === subbuzzData?.gateIndex}
          />
          <AdsSsrPlacement index={i} subbuzzes={displaySubbuzzes} />
        </React.Fragment>
      ))}
    </div>
  );
};

Subbuzzes.propTypes = {
  subbuzzData: PropTypes.shape({
    assets: PropTypes.object,
    subbuzzes: PropTypes.array,
    gateIndex: PropTypes.number,
  }),
};

export default withInlineAds(Subbuzzes);
